/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 학교 정보 관련 API
 * 버전 : 1.0
 * 파일명 : SchoolService.ts
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-08-21, 강동형 : 저작권 추가, AI 관련 API 추가
 * 수정 : 2026-01-27, 강동형 : postSchoolfilter에 major값 추가
 * 수정 : 2026-01-30, 강동형 : 학과기준 검색 API 추가
 * 수정 : 2026-02-02, 강동형 : 학교별 개인 메모 추가 API 추가
 * ================================================================ */

import axios from 'axios';
import { getAuthToken } from './Auth';
import { SchoolFilterType, ExclusionType } from 'src/types/SchoolType'
import { useSearchStore } from 'src/stores/SearchStore';

const API_URL = process.env.REACT_APP_API_URL

export const postSchoolfilter = async ({page, sort_by,sort_order,school_name,months_last_visit,loc,edu_level,highschool_type,special_note}:SchoolFilterType)=>{
    try{
        const token = getAuthToken()
        
        const response = await axios.post(`${API_URL}/search-schools`,{
            school_name,
            months_last_visit,
            loc,
            edu_level,
            highschool_type,
            special_note
        },{
            params:{
                page: page,
                sort_by: sort_by, // school_name, visited_at, total_student_cnt //Default value : school_name
                sort_order: sort_order, //'asc' 또는 'desc' //Default value : asc
                page_size : 10,
                major:1
            },
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }
        })
        return response.data
    }
    catch(error){
        // console.error("🚨 데이터 요청 실패:", error);
        if(axios.isAxiosError(error)){
            if(error.response){
                const response = error.response
                console.error(response.data);
                return response.data
            }
        }
      }
    }

export const patchExclustion = async ({school_class, school_id, excluded}: ExclusionType)=>{
    //스쿨클래스 수정
    try{
        const token = getAuthToken()
        if(school_class === "고등학교"){
            const response = await axios.patch(`${API_URL}/highschool-exclusion-status`,{
                school_id,
                excluded
            },{
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
        else if (school_class === "대학교"){
            const response = await axios.patch(`${API_URL}/university-exclusion-status`,{
                school_id,
                excluded
            },{
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
        else if (school_class === "기타"){
            const response = await axios.patch(`${API_URL}/tc-exclusion-status`,{
                school_id,
                excluded
            },{
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
    }
    catch(error){
        console.error("🚨 데이터 요청 실패:", error);
        if(axios.isAxiosError(error)){
            if(error.response){
                const response = error.response
                return response.data
            }
        }
    }
}

export const getPromoResult = async(school_class:string,school_id:number)=>{
    try{
        const token = getAuthToken()
        if (school_class==='고등학교'){
            const response = await axios.get(`${API_URL}/highschool-promo-result`,{
                params:{
                    school_id: school_id
                },
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
        else if (school_class === '대학교'){
            const response = await axios.get(`${API_URL}/university-promo-result`,{
                params:{
                    education_id: school_id
                },
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
        else{
            const response = await axios.get(`${API_URL}/tc-promo-result`,{
                params:{
                    tc_id: school_id
                },
                headers:{
                    'Content-Type' : 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })
            return response.data
        }
    }
    catch (error){
        console.error("🚨 데이터 요청 실패:", error);

        // ✅ 서버 응답이 있는 경우 (예: 400, 401, 500 등)
        if (axios.isAxiosError(error) && error.response) {
          return "서버 오류 발생"
        }
    
        // ✅ 서버 응답이 없는 경우 (네트워크 오류 등)
        return "네트워크 오류 또는 서버 응답 없음"
    }
}

interface getSearchSchoolGeoCenterParams{
    min_lon: number,
    min_lat: number,
    max_lon: number,
    max_lat: number,
    school_name: string,
    months_last_visit: number | string,
    edu_level: number[],
    highschool_type: number[],
    special_note: number[]
}

export const getSearchSchoolGeoCenter = async (params:getSearchSchoolGeoCenterParams)=>{
  try{
        const token = getAuthToken()
        const response = await axios.post(`${API_URL}/search-schools-geo`,
            {
                min_lon: params.min_lon,
                min_lat: params.min_lat,
                max_lon: params.max_lon,
                max_lat: params.max_lat,
                school_name: params.school_name,
                months_last_visit: params.months_last_visit,
                edu_level: params.edu_level,
                highschool_type: params.highschool_type,
                special_note: params.special_note
            },{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

interface getStrategicInfoParams{
    school_id: number,
    is_highschool: boolean
}

export const getStrategicInfo = async (params:getStrategicInfoParams)=>{
  try{
        const token = getAuthToken()
        if(params.is_highschool){
        const response = await axios.get(`${API_URL}/strategic-info/highschool/${params.school_id}/advice`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
        }
        else{
            const response = await axios.get(`${API_URL}/strategic-info/university/${params.school_id}/advice`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
        }
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const postRecommendRoles = async (school_class:string | undefined, school_id:number | undefined)=>{
    try{
        const token = getAuthToken()
        
        const response = await axios.post(`${API_URL}/ai-recommend-roles`,{},{
            params:{
                school_class:school_class,
                school_id:school_id
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            return response.data
        }
        catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
                const response = error.response
                return response.data
            }
        }
    } 
}

type getSchoolMajorType = {
    major_name:string,
}

export const getSchoolMajor = async ({major_name}:getSchoolMajorType)=>{
    try{
        const token = getAuthToken()
        const {setField, pageNum} = useSearchStore.getState()
        
        const response = await axios.get(`${API_URL}/search-by-major`,{
            params:{
                major_name:major_name,
                page:pageNum,
                page_size:20
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            setField('totalRow',response.data.total_cnt)
            const nextPage = pageNum + 1;
            setField('pageNum', nextPage);
            return response.data
        }
        catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
                const response = error.response
                return response.data
            }
        }
    } 
}

type postSchoolMemoType = {
    school_id: number,
    school_class: string,
    content: string
}

export const postSchoolMemo = async ({ school_id,school_class,content }:postSchoolMemoType)=>{
    try{
        const token = getAuthToken()
        
        const response = await axios.post(`${API_URL}/upsert-school-memo`,{
            school_id: school_id,
            school_class: school_class,
            content: content
        },{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            return response.data
        }
        catch(error){
            if(axios.isAxiosError(error)){
                if(error.response){
                    const response = error.response
                    return response.data
                }
            }
    } 
}